import { useState, useEffect } from "react";

const STORAGE_PREFIX = "pizza_preferences_user_";

const loadFromStorage = (storageKey: string | null): Set<number> | null => {
  if (!storageKey) return null;
  const saved = localStorage.getItem(storageKey);
  if (!saved) return null;
  const ids = JSON.parse(saved);
  return Array.isArray(ids) ? new Set(ids) : null;
};

export const usePizzaPreferences = (userId: number | null) => {
  const storageKey = userId ? `${STORAGE_PREFIX}${userId}` : null;

  const [visiblePizzaIds, setVisiblePizzaIds] = useState<Set<number> | null>(
    () => {
      return loadFromStorage(storageKey);
    }
  );

  useEffect(() => {
    if (storageKey && visiblePizzaIds !== null) {
      localStorage.setItem(
        storageKey,
        JSON.stringify(Array.from(visiblePizzaIds))
      );
      window.dispatchEvent(
        new CustomEvent("pizzaPreferencesChanged", {
          detail: { userId, visiblePizzaIds: Array.from(visiblePizzaIds) },
        })
      );
    } else if (storageKey && visiblePizzaIds === null) {
      localStorage.removeItem(storageKey);
      window.dispatchEvent(
        new CustomEvent("pizzaPreferencesChanged", {
          detail: { userId, visiblePizzaIds: null },
        })
      );
    }
  }, [visiblePizzaIds, storageKey, userId]);

  useEffect(() => {
    if (!storageKey) return;

    const handleStorageChange = (e: StorageEvent | CustomEvent) => {
      if (e instanceof StorageEvent) {
        if (e.key === storageKey) {
          setVisiblePizzaIds(loadFromStorage(storageKey));
        }
      } else if (e instanceof CustomEvent) {
        if (e.detail.userId === userId) {
          const newIds = e.detail.visiblePizzaIds as number[] | null;
          setVisiblePizzaIds((current) => {
            if (newIds === null && current === null) return current;
            if (newIds !== null && Array.isArray(newIds) && current !== null) {
              const newSet = new Set(newIds as number[]);
              const newIdsArray = newIds as number[];
              if (
                newSet.size === current.size &&
                newIdsArray.every((id) => current.has(id))
              ) {
                return current;
              }
            }
            return newIds === null ? null : new Set(newIds);
          });
        }
      }
    };

    window.addEventListener(
      "pizzaPreferencesChanged",
      handleStorageChange as EventListener
    );
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener(
        "pizzaPreferencesChanged",
        handleStorageChange as EventListener
      );
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [storageKey, userId]);

  const togglePizzaVisibility = (pizzaId: number) => {
    setVisiblePizzaIds((prev) => {
      const newSet = prev ? new Set(prev) : new Set<number>();
      if (newSet.has(pizzaId)) {
        newSet.delete(pizzaId);
      } else {
        newSet.add(pizzaId);
      }
      return newSet;
    });
  };

  const setAllVisible = (pizzaIds: number[]) => {
    setVisiblePizzaIds(
      pizzaIds.length > 0 ? new Set(pizzaIds) : new Set<number>()
    );
  };

  const isPizzaVisible = (pizzaId: number) => {
    if (visiblePizzaIds === null) return true;
    if (visiblePizzaIds.size === 0) return false;

    return visiblePizzaIds.has(pizzaId);
  };

  return {
    visiblePizzaIds,
    togglePizzaVisibility,
    setAllVisible,
    isPizzaVisible,
  };
};
